import React, { useEffect, useState, useCallback, useLayoutEffect } from 'react';
import { Switch, Route, Redirect, useLocation, useHistory } from 'react-router-dom';
import { ConfigProvider, Layout, message, theme as antTheme } from 'antd';
import { StyleProvider } from '@ant-design/cssinjs';
import { useDispatch } from 'react-redux';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// i18n
import './i18n';
import { useTranslation } from 'react-i18next';

// Components
import Header from './Component/header';
import SideModal from './Component/sideModal';
import RouterWrapper from './router/routerWrapper';
import Login from './views/Login';
import ForgotPassword from './views/ForgotPassword';

// Notifications
import { NotificationProvider } from './contexts/NotificationContext';

// Permissions
import { PermissionProvider } from './contexts/PermissionContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';

// Styles
import './styles/index.scss';
import './styles/_reset.scss';
import './App.scss';

// Redux
import { setUserInfo } from './redux/action';

// Network
import Api from './Network';

// Types
import { UserInfo } from './types/types';

const { Content, Sider } = Layout;
const { defaultAlgorithm, darkAlgorithm } = antTheme;

const DESKTOP_SIDEBAR_WIDTH = 250;
const DESKTOP_SIDEBAR_COLLAPSED_WIDTH = 80;
const SIDEBAR_COLLAPSED_STORAGE_KEY = 'dashboard.sidebarCollapsed';

const readPersistedSidebarCollapsed = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  return localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true';
};

const persistSidebarCollapsed = (collapsed: boolean): void => {
  if (typeof window === 'undefined') {
    return;
  }

  localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(collapsed));
};

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

const AppShell: React.FC = () => {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    if (window.innerWidth <= 768) {
      return true;
    }

    return readPersistedSidebarCollapsed();
  });
  const [isMobileViewport, setIsMobileViewport] = useState(() => window.innerWidth <= 768);
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const { themeMode, isDark } = useTheme();
  const [loading, setLoading] = useState(true);

  const handleLoadUserData = useCallback(async () => {
    // Only try to load user data if there's a token
    const token = localStorage.getItem('token');

    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const userResponse: any = await Api.get('/api/admin/me');

      if (
        userResponse &&
        userResponse.status !== false &&
        userResponse.data &&
        userResponse.data.user
      ) {
        dispatch(setUserInfo(userResponse.data));
        setUser(userResponse.data.user);
      } else {
        console.error('Me endpoint failed - invalid response:', userResponse);
        setUser(null);
        localStorage.removeItem('token');
        if (location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    } catch (error: any) {
      console.error('Me endpoint error:', error); // Debug log
      // Don't show error message if we're already on login page
      if (location.pathname !== '/login') {
        message.error('Failed to load user data');
      }
      setUser(null);
      localStorage.removeItem('token');
      if (location.pathname !== '/login') {
        window.location.href = '/login';
      }
    } finally {
      setLoading(false);
    }
  }, [dispatch, location.pathname]);

  useLayoutEffect(() => {
    // Always try to load user data on mount
    const token = localStorage.getItem('token');

    if (token) {
      // If we have a token, always try to load user data
      handleLoadUserData();
    } else {
      // If no token, set loading to false immediately
      setLoading(false);
    }

    // If user is logged in but on login page, redirect to home
    if (location.pathname === '/login' && user) {
      history.push('/');
    }
  }, []); // Only run once on mount

  const handleLogout = async () => {
    try {
      await Api.post('/api/admin/logout');
      message.success('Logged out successfully');
      setUser(null);
      window.location.href = '/login';
    } catch (err) {
      message.error('Logout failed');
    }
  };

  const handleMenuToggle = () => {
    if (isMobileViewport) {
      setMobileMenuOpen((prev) => !prev);
      return;
    }

    setDesktopSidebarCollapsed((prev) => {
      const next = !prev;
      persistSidebarCollapsed(next);
      return next;
    });
  };

  const handleDesktopSidebarCollapsedChange = (collapsed: boolean) => {
    setDesktopSidebarCollapsed(collapsed);
    persistSidebarCollapsed(collapsed);
  };

  const handleMenuClose = () => {
    setMobileMenuOpen(false);
  };

  useEffect(() => {
    const handleResize = () => {
      const nextIsMobile = window.innerWidth <= 768;
      setIsMobileViewport(nextIsMobile);
      if (nextIsMobile) {
        setDesktopSidebarCollapsed(true);
      } else {
        setMobileMenuOpen(false);
        setDesktopSidebarCollapsed(readPersistedSidebarCollapsed());
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initialize RTL support
  useEffect(() => {
    const currentLang = i18n.language;

    if (currentLang === 'ar') {
      document.documentElement.dir = 'rtl';
      document.documentElement.lang = 'ar';
    } else {
      document.documentElement.dir = 'ltr';
      document.documentElement.lang = 'en';
    }
  }, [i18n.language]);

  const ScrollToTop = () => {
    useEffect(() => {
      const unListen = history.listen(() => {
        window.scrollTo(0, 0);
      });

      return () => {
        unListen();
      };
    }, [history]);

    return null;
  };

  // Check if current language is Arabic
  const isRTL = i18n.language === 'ar';
  const desktopSidebarWidth = !isMobileViewport
    ? desktopSidebarCollapsed
      ? DESKTOP_SIDEBAR_COLLAPSED_WIDTH
      : DESKTOP_SIDEBAR_WIDTH
    : 0;
  const contentMarginLeft = isRTL ? 0 : desktopSidebarWidth;
  const contentMarginRight = isRTL ? desktopSidebarWidth : 0;

  // Safely import Ant Design locales
  const getAntLocale = () => {
    try {
      if (isRTL) {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        return require('antd/locale/ar_EG').default;
      } else {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        return require('antd/locale/en_US').default;
      }
    } catch (error) {
      // Failed to load Ant Design locale
    }
  };

  if (loading) {
    return (
      <div className="app-splash">
        <div className="app-splash__inner">
          <div className="app-splash__ring" />
          <div className="app-splash__title">Loading application...</div>
          <div className="app-splash__sub">Preparing your workspace</div>
          <div style={{ marginTop: '28px' }}>
            <button
              type="button"
              className="app-splash__clear"
              onClick={() => {
                localStorage.clear();
                window.location.reload();
              }}
            >
              Clear Cache & Reload
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`App ${themeMode}-theme premium-admin${isDark ? ' is-dark' : ''}`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <StyleProvider hashPriority="high">
        <ConfigProvider
          theme={{
            algorithm: isDark ? darkAlgorithm : defaultAlgorithm,
            token: {
              colorPrimary: isDark ? '#818cf8' : '#6366f1',
              colorInfo: '#0ea5e9',
              colorSuccess: '#10b981',
              colorWarning: '#f59e0b',
              colorError: '#ef4444',
              colorTextBase: isDark ? '#e2e8f0' : '#1e293b',
              colorBgLayout: isDark ? '#0a0a14' : '#e4e8f4',
              colorBgContainer: isDark ? '#1a1a2e' : '#ffffff',
              colorBgElevated: isDark ? '#1e1e32' : '#ffffff',
              colorBorder: isDark ? 'rgba(148, 163, 184, 0.18)' : 'rgba(148, 163, 184, 0.22)',
              colorBorderSecondary: isDark
                ? 'rgba(148, 163, 184, 0.1)'
                : 'rgba(148, 163, 184, 0.14)',
              borderRadius: 12,
              borderRadiusLG: 22,
              borderRadiusSM: 10,
              fontFamily: isRTL
                ? "'Cairo', Tahoma, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                : "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
              fontSize: 14,
              boxShadow: isDark
                ? '0 8px 24px rgba(0, 0, 0, 0.35)'
                : '0 8px 24px rgba(15, 23, 42, 0.08)',
              boxShadowSecondary: isDark
                ? '0 24px 48px rgba(0, 0, 0, 0.45)'
                : '0 24px 48px rgba(15, 23, 42, 0.12)',
              wireframe: false,
            },
            components: {
              Button: {
                controlHeight: 42,
                controlHeightLG: 48,
                borderRadius: 12,
                fontWeight: 600,
                primaryShadow: isDark
                  ? '0 10px 28px rgba(99, 102, 241, 0.35)'
                  : '0 10px 28px rgba(91, 94, 247, 0.45)',
                defaultShadow: isDark
                  ? '0 2px 8px rgba(0, 0, 0, 0.25)'
                  : '0 2px 8px rgba(15, 23, 42, 0.06)',
              },
              Card: {
                borderRadiusLG: 22,
                boxShadowTertiary: isDark
                  ? '0 16px 40px rgba(0, 0, 0, 0.35)'
                  : '0 16px 40px rgba(15, 23, 42, 0.08)',
              },
              Table: {
                headerBg: isDark ? '#1e1e32' : '#f8fafc',
                headerColor: isDark ? '#cbd5e1' : '#475569',
                headerSplitColor: isDark ? 'rgba(129, 140, 248, 0.45)' : 'rgba(91, 94, 247, 0.55)',
                borderColor: isDark ? 'rgba(148, 163, 184, 0.12)' : 'rgba(148, 163, 184, 0.16)',
                rowHoverBg: isDark ? 'rgba(99, 102, 241, 0.12)' : '#f5f3ff',
                rowSelectedBg: isDark ? 'rgba(99, 102, 241, 0.18)' : '#ede9fe',
                cellPaddingBlock: 13,
                cellPaddingInline: 16,
              },
              Menu: {
                itemBorderRadius: 12,
                itemHeight: 48,
              },
              Modal: {
                borderRadiusLG: 22,
                headerBg: isDark ? '#1a1a2e' : '#ffffff',
                titleColor: isDark ? '#f1f5f9' : '#0f172a',
                contentBg: isDark ? '#1a1a2e' : '#ffffff',
              },
              Input: {
                controlHeight: 42,
                borderRadius: 12,
              },
              Select: {
                controlHeight: 42,
                borderRadius: 12,
              },
              DatePicker: {
                controlHeight: 42,
                borderRadius: 12,
              },
              Tag: {
                borderRadiusSM: 999,
              },
              Dropdown: {
                colorBgElevated: isDark ? '#1e1e32' : '#ffffff',
              },
            },
          }}
          direction={isRTL ? 'rtl' : 'ltr'}
          locale={getAntLocale()}
        >
          <QueryClientProvider client={queryClient}>
            <NotificationProvider>
              <PermissionProvider>
                <Switch>
                  <Route
                    path="/login"
                    render={() => <Login setUser={setUser} onLoginSuccess={handleLoadUserData} />}
                  />
                  <Route path="/forgot-password" component={ForgotPassword} />
                  <Route
                    path="/"
                    render={() =>
                      user ? (
                        <Layout style={{ minHeight: '100vh' }}>
                          <Layout style={{ display: 'flex', minHeight: '100vh' }}>
                            {/* Desktop Sidebar */}
                            {!isMobileViewport && (
                              <Sider
                                className={`desktop-sidebar premium-sidebar${desktopSidebarCollapsed ? ' premium-sidebar--collapsed' : ''}`}
                                width={DESKTOP_SIDEBAR_WIDTH}
                                collapsed={desktopSidebarCollapsed}
                                collapsedWidth={DESKTOP_SIDEBAR_COLLAPSED_WIDTH}
                                trigger={null}
                                style={{
                                  position: 'fixed',
                                  height: '100vh',
                                  top: 0,
                                  [isRTL ? 'right' : 'left']: 0,
                                  zIndex: 100,
                                }}
                              >
                                <SideModal
                                  embedded
                                  collapsed={desktopSidebarCollapsed}
                                  onCollapsedChange={handleDesktopSidebarCollapsedChange}
                                  isOpen
                                  onClose={() => handleDesktopSidebarCollapsedChange(true)}
                                />
                              </Sider>
                            )}

                            {/* Mobile Sidebar */}
                            {isMobileViewport && (
                              <SideModal isOpen={mobileMenuOpen} onClose={handleMenuClose} />
                            )}

                            <Layout
                              className="site-layout"
                              style={{
                                flex: 1,
                                marginLeft: contentMarginLeft,
                                marginRight: contentMarginRight,
                              }}
                            >
                              <Header
                                onLogout={handleLogout}
                                onMenuToggle={handleMenuToggle}
                                sidebarCollapsed={desktopSidebarCollapsed}
                                isMobileViewport={isMobileViewport}
                              />
                              <Content className="responsive-content site-layout-background app-mesh-bg content-island">
                                <ScrollToTop />
                                <RouterWrapper />
                              </Content>
                            </Layout>
                          </Layout>
                        </Layout>
                      ) : (
                        <Redirect to="/login" />
                      )
                    }
                  />
                </Switch>
              </PermissionProvider>
            </NotificationProvider>
          </QueryClientProvider>
        </ConfigProvider>
      </StyleProvider>
    </div>
  );
};

const App: React.FC = () => (
  <ThemeProvider>
    <AppShell />
  </ThemeProvider>
);

export default App;
